import MultiSelect from '../../../../src/components/formControllers/multiSelect';
import { useFormWithSchema } from '../../../../src/utils/functions/yupFormTypescript';
import React, { useEffect, useState } from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import './index.scss';
import { OpenInFull } from '@mui/icons-material';
// import GoogleMapView from '../map/GoogleMapView';
import {
  mapFormSchema,
  MVPFiltersProps,
} from '../constants';
import { CustomDialog } from '../../../../src/components/CustomDialog';
import MapOnDialogView from './mapDialog';
import {
  useGetFieldDataForFieldNosQuery,
  useGetFilterDataForMapQuery,
} from '../../../services/mVPMapService';
import dayjs from 'dayjs';
import { TDialogFieldMapData } from '../../../types/mvpMap';
import { useAlertHelper } from '../../../utils/functions/hooks';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import { AppCircularProgress } from '../../../components/common/loaders/AppCircularProgress';
import { LegendScale } from '../legend-scale';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  AutoCompleteController,
  DatePickerController,
  InputController,
} from '../../../components';
import { useFilterContext } from '../../../services/mVPMapService/filterContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useDebounce } from '../../../services/customHook/debounceHook';
import GoogleMapViewComparative from './GoogleMapViewComparative';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export const MapView: React.FC<MVPFiltersProps> = ({
  onBack,
}) => {
  const [open, setOpen] = useState(false);
  const [dialogFieldMapData, setDialogFieldMapData] =
    useState<TDialogFieldMapData>();
  const { showError } = useAlertHelper();

  const openDialog = (value: boolean) => {
    setOpen(value);
  };
  const { filterValues: commonFilters } =
    useFilterContext();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormWithSchema(mapFormSchema, {
    mode: 'onTouched',
  });

  // Store current time in a ref to persist across renders

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    const month = now.getMonth() + 1; // getMonth() is 0-indexed
    const year = now.getFullYear();
    return `${year}-${month < 10 ? '0' + month : month}`;
  });

  const handleVisibleMonthChange = (date: Date) => {
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    setCurrentMonth(
      `${year}-${month < 10 ? '0' + month : month}`
    );
  };

  const baseDate = dayjs(
    `${currentMonth}-01`,
    'MMMM YYYY-DD'
  ); // Ensure valid date in Safari

  const startOfPreviousMonth = baseDate
    .subtract(1, 'month')
    .startOf('month')
    .unix();

  const endOfNextMonth = baseDate
    .add(1, 'month')
    .endOf('month')
    .unix();

  const debouncedIntValue = useDebounce(
    Number(watch('intValue')),
    1000
  );

  const shouldFetchData =
    typeof debouncedIntValue === 'number' &&
    debouncedIntValue <= 200;

  const {
    data: filtersDataObject,
    isFetching: isLoadingFilters,
  } = useGetFilterDataForMapQuery(
    {
      commonFilters,
      wsId,
      startTime: startOfPreviousMonth,
      endTime: endOfNextMonth,
      filterTypes: ['FILTER_DATE'],
    },

    {
      skip: !commonFilters?.index || !commonFilters?.season,
    }
  );
  const dateOfData = watch('dateOfData');

  const startTime = dateOfData
    ? Math.floor(dayjs(dateOfData).startOf('day').unix())
    : 0;

  const endTime = dateOfData
    ? Math.floor(dayjs(dateOfData).endOf('day').unix())
    : 0;

  const { data: filtersDataObjectt } =
    useGetFilterDataForMapQuery(
      {
        commonFilters: commonFilters,
        wsId,
        startTime: startTime!,
        endTime: endTime!,
        filterTypes: ['FILTER_FIELD_NUMBER'],
        tableDataType: watch('date'),
        daysAfterPlanting: debouncedIntValue,
      },

      {
        skip:
          !commonFilters?.index ||
          !commonFilters?.season ||
          (watch('date') == 'CUSTOM_DATE' && !dateOfData) ||
          !shouldFetchData,
      }
    );

  const filtersData =
    filtersDataObjectt?.data
      ?.GetDataForTimeRangeWithFilters;
  const includeDatesList =
    filtersDataObject?.data?.GetDataForTimeRangeWithFilters?.dates?.map(
      (item) => new Date(item)
    );

  const [
    fieldNumberFilterOptions,
    setFieldNumberFilterOptions,
  ] = useState<{ value: number; label: string }[]>([]);

  const selectedDate = dateOfData
    ? dayjs(watch('dateOfData'))
    : null;
  const [selectedFarms, setSelectedFarms] =
    useState<number[]>();
  const [newSelectedFarm, setNewSelectedFarm] =
    useState<number>();

  useEffect(() => {
    if (includeDatesList?.length) {
      if (watch('dateOfData')) {
        const selectedDate =
          watch('dateOfData')?.toDateString();
        if (
          !includeDatesList.find(
            (item) => item?.toDateString() == selectedDate
          )
        ) {
          setValue('dateOfData', null);
        }
      }
    } else {
      setValue('dateOfData', null);
    }
    if (fieldNumberFilterOptions?.length) {
      setValue(
        'farmNumbers',
        selectedFarms?.filter((selectedFarm) =>
          fieldNumberFilterOptions?.find(
            (ele) => ele.value === selectedFarm
          )
        )
      );
    } else {
      setValue('farmNumbers', []);
    }
  }, [filtersDataObject, filtersDataObjectt]);

  useEffect(() => {
    const currentSelectedFarms = watch('farmNumbers') || [];

    const newFieldNumberOptions =
      filtersData?.fieldNumbers?.map((fieldNumber) => ({
        value: fieldNumber,
        label: fieldNumber.toString(),
      })) || [];

    const mergedFieldNumberOptions = [
      ...newFieldNumberOptions,
      ...currentSelectedFarms.map((farm) => ({
        value: farm,
        label: farm.toString(),
      })),
    ].filter(
      (option, index, self) =>
        self.findIndex((o) => o.value === option.value) ===
        index
    );

    // Reorder to keep selected fields on top
    const reorderedOptions = [
      ...currentSelectedFarms
        .map((farm) =>
          mergedFieldNumberOptions.find(
            (option) => option.value === farm
          )
        )
        .filter(
          (
            option
          ): option is { value: number; label: string } =>
            !!option
        ), // Ensure no undefined values
      ...mergedFieldNumberOptions.filter(
        (option) =>
          !currentSelectedFarms.includes(option.value)
      ),
    ];

    setFieldNumberFilterOptions(reorderedOptions); // Update dropdown options
  }, [filtersDataObjectt, filtersDataObject]);

  useEffect(() => {
    const farms = watch('farmNumbers') || [];

    const updatedFieldNumberOptions =
      filtersData?.fieldNumbers?.map((fieldNumber) => ({
        value: fieldNumber,
        label: fieldNumber.toString(),
      })) || [];

    const uniqueFieldOptions = [
      ...updatedFieldNumberOptions,
      ...farms.map((farm) => ({
        value: farm,
        label: farm.toString(),
      })),
    ].filter(
      (option, index, self) =>
        self.findIndex((o) => o.value === option.value) ===
        index
    );

    setFieldNumberFilterOptions(uniqueFieldOptions);
  }, [
    filtersDataObjectt,
    filtersDataObject,
    watch('farmNumbers'),
  ]);

  const queryFilters = (() => {
    if (
      (watch('date') == 'CUSTOM_DATE' &&
        selectedDate === null) ||
      selectedFarms === undefined ||
      selectedFarms?.length < 1
    ) {
      return undefined;
    }

    const startTime =
      Math.floor(
        Number(selectedDate?.startOf('day').utc()) / 1000
      ) || 0;
    const endTime =
      Math.floor(
        Number(selectedDate?.endOf('day').utc()) / 1000
      ) || 0;
    const tableDataType = watch('date');
    const daysAfterPlanting = debouncedIntValue;

    return {
      commonFilters,
      wsId,
      startTime,
      endTime,
      fieldNumbers: selectedFarms,
      tableDataType,
      daysAfterPlanting,
    };
  })();
  const {
    data: fieldDataForFieldNos,
    isFetching: isLoadingDataForFieldNos,
  } = useGetFieldDataForFieldNosQuery(
    queryFilters ?? skipToken
  );
  const fieldDataList = selectedFarms?.length
    ? fieldDataForFieldNos?.data?.GetFieldDataForFieldNos
        ?.fieldDataList
    : null;

  useEffect(() => {
    const farms = watch('farmNumbers');
    if (!farms || (farms && farms.length <= 6)) {
      const newSelectedFarm = farms?.find(
        (farm) => !selectedFarms?.includes(farm)
      );
      setNewSelectedFarm(newSelectedFarm);
      setSelectedFarms(farms);
    } else if (farms.length > 6) {
      showError(
        'you can select maximum 6 fields at a time'
      );
      setValue('farmNumbers', selectedFarms);
    }
  }, [watch('farmNumbers')]);

  const dateFieldOptions = [
    {
      value: 'CUSTOM_DATE',
      label: 'Date',
    },
    {
      value: 'DAY_AFTER_PLANTING_TYPE',
      label: 'Days after Planting',
    },
  ];

  return (
    <>
      <Box>
        {(isLoadingFilters || isLoadingDataForFieldNos) && (
          <AppCircularProgress />
        )}
        <div style={{ marginTop: 10 }}>
          <IconButton
            className="close-icon-btn"
            type="button"
            onClick={onBack}>
            <ArrowBackIcon className="close-icon" />
          </IconButton>
          Back
        </div>
        <Grid container spacing={2} padding={2}>
          <Grid
            item
            xs={6}
            sm={6}
            md={2}
            lg={2}
            className="field-grid">
            <AutoCompleteController
              label="Type"
              name="date"
              control={control}
              options={dateFieldOptions}
              error={errors.date?.message}
              disableClearable
            />
          </Grid>
          {watch('date') == 'CUSTOM_DATE' && (
            <Grid
              item
              xs={12}
              sm={12}
              md={3}
              lg={2}
              className="field-grid">
              <DatePickerController
                label="Date"
                control={control}
                required
                name="dateOfData"
                error={errors?.dateOfData?.message}
                includeDates={includeDatesList}
                onVisibleMonthChange={
                  handleVisibleMonthChange
                }
              />
            </Grid>
          )}
          {watch('date') === 'DAY_AFTER_PLANTING_TYPE' && (
            <Grid
              item
              xs={6}
              sm={6}
              md={2}
              lg={2}
              className="field-grid">
              <InputController
                label="Number of Days"
                register={register('intValue')}
                type="number"
                required
                error={errors.intValue?.message}
              />
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={12}
            md={3}
            lg={2}
            className="field-grid">
            <MultiSelect
              label="Select Farm"
              name="farmNumbers"
              control={control}
              options={fieldNumberFilterOptions}
              error={errors.farmNumbers?.message}
              placeholder="Farm Number"
              showSelectAll={false}
            />
          </Grid>
        </Grid>
        <Box className="grid-title title-text-container">
          Comparing {commonFilters?.index}
        </Box>
        <Grid container>
          {selectedFarms?.map((selectedFarm) => {
            const item = fieldDataList?.find(
              (field) => field.fieldNumber == selectedFarm
            );
            const fieldNumber = selectedFarm;
            const isLoadingFarmData =
              isLoadingDataForFieldNos &&
              newSelectedFarm === selectedFarm;
            const fieldIndexData =
              item?.fieldIndexList?.[0];
            const tiffFileUrl =
              fieldIndexData?.presignedS3TiffUrl;
            const geometryHex =
              item?.fieldGeomData?.geometry;
            const averageIndex =
              fieldIndexData?.averageIndex;
            const cloudCoverPercentage =
              fieldIndexData?.cloudCoverPercentage;
            return (
              <Grid
                key={fieldNumber}
                item
                xs={12}
                sm={12}
                md={4}
                lg={4}
                className="grid-item-height">
                <Box className="grid-item-child">
                  {!fieldNumber ? (
                    <Box className="no-farm-selected">
                      No farm selected
                    </Box>
                  ) : (
                    <Box sx={{ height: '100%' }}>
                      <Box>
                        <Grid container>
                          <Grid item xs={7}>
                            <Box
                              component={'span'}
                              className="grid-item-title">
                              Farm {fieldNumber}
                            </Box>
                            <Box
                              component={'span'}
                              className="grid-item-title">
                              {averageIndex?.toFixed(2)}
                            </Box>
                          </Grid>
                          <Grid item xs={5}>
                            <IconButton
                              className="float-right title-icons"
                              title="Open"
                              onClick={() => {
                                openDialog(true);
                                tiffFileUrl &&
                                  geometryHex &&
                                  setDialogFieldMapData({
                                    tiffFileUrl,
                                    geometryHex,
                                    fieldNumber,
                                    averageIndex,
                                    cloudCoverPercentage,
                                    indexName:
                                      commonFilters?.index,
                                  });
                              }}>
                              <OpenInFull />
                            </IconButton>
                            <div className="flex items-center float-right mr-3 title-icons">
                              <IconButton
                                className="float-right title-icons"
                                title="Cloud Cover Percentage">
                                <CloudQueueIcon />
                              </IconButton>
                              {cloudCoverPercentage}
                            </div>
                          </Grid>
                        </Grid>
                      </Box>
                      {!tiffFileUrl || !geometryHex ? (
                        !isLoadingFarmData && (
                          <>
                            <Box
                              sx={{
                                position: 'absolute',
                                margin: '8px 12px',
                                background: '#fff',
                                padding: '4px 8px',
                                zIndex: '1',
                                borderRadius: '4px',
                              }}>
                              {dayjs(
                                watch('dateOfData')
                              ).format('DD/MM/YYYY')}
                            </Box>
                            <Box className="no-data-available">
                              {!tiffFileUrl
                                ? 'No Data Available for the selected Date'
                                : 'Polygon data not available'}
                            </Box>
                          </>
                        )
                      ) : (
                        <Box className="map-container">
                          {
                            <GoogleMapViewComparative
                              tiffFileUrl={tiffFileUrl}
                              geometryHex={geometryHex}
                              indexName={
                                commonFilters?.index
                              }
                              selectedDate={
                                watch('dateOfData')
                                  ? dayjs(
                                      watch('dateOfData')
                                    )
                                  : undefined
                              }
                            />
                          }
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
        {!fieldDataList?.length && (
          <div className="text-center mt-2 no-data-text">
            {selectedFarms?.length && dateOfData
              ? 'No Data Found'
              : 'Please select date and field numbers to compare data'}
          </div>
        )}
      </Box>
      {open && dialogFieldMapData && (
        <CustomDialog
          maxWidth="xl"
          open={open}
          setOpen={openDialog}
          dialogTitle={`Farm ${dialogFieldMapData.fieldNumber}`}>
          <MapOnDialogView {...dialogFieldMapData} />
        </CustomDialog>
      )}
      <Box>
        <LegendScale
          filterValues={commonFilters}
          showData={true}
          selectedIndex={commonFilters?.index}
        />
      </Box>
    </>
  );
};
